import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, getal } from '../i18n.js';
import { St } from './Status.jsx';

/**
 * Bron › Blokkeren (docs/besluiten/blokkades-bouwen.md): is deze firewall klaar om op te blokkeren? Drie dingen, live van
 * de FMC: een blokkeergebruiker op de FMC, het dynamic object `ZERO-BLOCK-<firewall>`, en een blokkeerregel met dat
 * object bovenaan het toegangsbeleid. De regel zet een engineer er één keer in (met een deploy); daarna schrijft ZERO
 * alleen nog in het object, zonder deploy.
 */
export default function BlokInrichting({ bron, mij }) {
  const q = useQuery({ queryKey: ['blokinrichting', bron.id], queryFn: () => api.blokInrichting(bron.id), refetchOnWindowFocus: false });
  const [fout, zetFout] = useState(null);
  const [bezig, zetBezig] = useState(false);
  const mag = mij.permissions.includes('bronnen.beheren');
  const code = (x) => <code>{x}</code>;
  const d = q.data?.data;
  const regelOk = d && (d.rule?.van || d.rule?.naar);
  return (
    <div className="grid-2">
      <section className="card">
        <header><h2>{t('Klaar om te blokkeren?')}</h2>
          <span className="meta"><button type="button" className="namelink" onClick={() => q.refetch()} disabled={q.isFetching}>{q.isFetching ? t('De FMC vragen…') : t('Opnieuw nakijken')}</button></span></header>
        {fout && <div className="melder rood" style={{ marginBottom: 10 }}>{fout}</div>}
        {q.isPending ? <p className="muted">{t('De FMC vragen…')}</p> : q.isError ? <div className="melder rood">{t(q.error.message)}</div> : (
          <ul className="checks">
            <li><St soort={d.hasUser ? 'good' : 'warning'} /><span>{d.hasUser ? t('Blokkeergebruiker op {fmc}', { fmc: d.fmc }) : t('Nog geen blokkeergebruiker op {fmc}: stel hem in bij die FMC, onder API.', { fmc: d.fmc })}</span></li>
            <li><St soort={d.objectExists ? 'good' : d.status === 'mislukt' ? 'critical' : 'warning'} /><span>
              {d.objectExists ? <>{t('Dynamic object')} {code(d.object)}{t(', met nu {n} adres(sen)', { n: getal(d.inObject ?? 0) })}</>
                : d.error ? d.error : <>{t('Het dynamic object')} {code(d.object)} {t('bestaat nog niet.')}
                  {mag && d.hasUser && <> <button type="button" className="namelink" disabled={bezig} onClick={async () => {
                    zetFout(null); zetBezig(true);
                    try { await api.maakBlokObject(bron.id); await q.refetch(); } catch (e) { zetFout(t(e.message)); } finally { zetBezig(false); }
                  }}>{bezig ? t('Bezig…') : t('Nu aanmaken')}</button></>}</>}</span></li>
            <li><St soort={regelOk ? 'good' : 'warning'} /><span>
              {regelOk ? <>{t('In het toegangsbeleid {beleid}:', { beleid: d.rule.beleid })} {[d.rule.van && t('inkomend ({regel})', { regel: d.rule.van }), d.rule.naar && t('uitgaand ({regel})', { regel: d.rule.naar })].filter(Boolean).join(', ')}</>
                : d.rule?.fout ?? t('Nog geen blokkeerregel met dit object in het toegangsbeleid{beleid}.', { beleid: d.rule?.beleid ? ` ${d.rule.beleid}` : '' })}</span></li>
          </ul>
        )}
        <p className="noot">{t('Zonder regel zet ADTS ZERO het adres niet in het object: dan zou het lijken alsof er geblokkeerd wordt, terwijl de firewall niets tegenhoudt.')}</p>
      </section>
      <section className="card">
        <header><h2>{t('Eenmalig inrichten')}</h2></header>
        <ol className="steps">
          <li>{t('Op de FMC een gebruiker voor ADTS ZERO, bijvoorbeeld {naam}, met een eigen rol: alleen Object Manager (dynamic objects) en het lezen van Access Control Policy en Devices. Stel hem in bij de FMC in ADTS ZERO, onder API › Blokkeergebruiker.', { naam: 'zero-block' })}</li>
          <li>{t('Het dynamic object {object}: met Nu aanmaken hiernaast, of zelf op de FMC (Objects › Object Management › Dynamic Objects, type IP).', { object: d?.object ?? `ZERO-BLOCK-${bron.name}` })}</li>
          <li>{t('In het toegangsbeleid van {fw}, bovenaan (vóór de andere regels): een regel ZERO-BLOCK in, Action Block, Source Dynamic Objects {object}; en een regel ZERO-BLOCK uit, Action Block with reset, Destination Dynamic Objects {object}. Logging aan het begin van de verbinding.', { fw: bron.name, object: d?.object ?? `ZERO-BLOCK-${bron.name}` })}</li>
          <li>{t('Deploy, één keer. Daarna zet ADTS ZERO adressen in het object en haalt ze eruit zonder deploy.')}</li>
        </ol>
      </section>
    </div>
  );
}
